/**
 * Utility for parsing and formatting financial data extracted by AI.
 */

export type ReportingUnit = 'absolute' | 'k' | 'M' | 'B' | 'Cr';

/**
 * Converts a value from one unit to another.
 * @param value - The numeric value to convert
 * @param fromUnit - Source unit ('absolute', 'k', 'M', 'B', 'Cr')
 * @param toUnit - Target unit ('absolute', 'k', 'M', 'B', 'Cr')
 * @returns Value converted to target unit
 */
export const convertUnit = (value: number, fromUnit: ReportingUnit, toUnit: ReportingUnit): number => {
    if (fromUnit === toUnit) return value;
    
    // First convert to absolute (base unit)
    let absoluteValue = value;
    if (fromUnit === 'k') absoluteValue = value * 1000;
    else if (fromUnit === 'M') absoluteValue = value * 1000000;
    else if (fromUnit === 'B') absoluteValue = value * 1000000000;
    else if (fromUnit === 'Cr') absoluteValue = value * 10000000; // 1 Crore = 10 Million = 10,000,000
    // 'absolute' is already in absolute
    
    // Then convert from absolute to target unit
    if (toUnit === 'k') return absoluteValue / 1000;
    if (toUnit === 'M') return absoluteValue / 1000000;
    if (toUnit === 'B') return absoluteValue / 1000000000;
    if (toUnit === 'Cr') return absoluteValue / 10000000;
    // 'absolute' - return as is
    return absoluteValue;
};

/**
 * Formats a value with unit suffix
 * @param value - The numeric value to format
 * @param unit - The unit to use ('absolute', 'k', 'M', 'B', 'Cr')
 * @param currency - Currency code ('USD', 'INR', 'EUR', 'GBP')
 * @param decimals - Number of decimal places
 * @returns Formatted string with currency symbol and unit suffix
 */
export const formatWithUnit = (value: number, unit: ReportingUnit, currency: string = "USD", decimals: number = 1): string => {
    const symbolMap: { [key: string]: string } = {
        'USD': '$',
        'INR': '₹',
        'EUR': '€',
        'GBP': '£'
    };
    
    const symbol = symbolMap[currency] || currency + " ";
    const useIndianCommas = currency === 'INR' || unit === 'Cr';
    
    // Format absolute values with appropriate numbering system
    if (unit === 'absolute') {
        if (useIndianCommas) {
            return `${symbol}${formatIndianNumber(Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals))}`;
        }
        const formattedValue = value.toFixed(decimals);
        return `${symbol}${addWesternCommas(formattedValue)}`;
    }
    
    // For M/B/K/Cr units, add commas to the integer part of the number
    const formattedValue = value.toFixed(decimals);
    const withCommas = useIndianCommas 
        ? formatIndianDecimal(formattedValue) 
        : addWesternCommas(formattedValue);
    
    if (unit === 'k') return `${symbol}${withCommas}K`;
    if (unit === 'M') return `${symbol}${withCommas}M`;
    if (unit === 'B') return `${symbol}${withCommas}B`;
    if (unit === 'Cr') return `${symbol}${withCommas} Cr`;
    
    return `${symbol}${withCommas}`;
};

/**
 * Adds Western-style commas (1,234,567.89)
 */
const addWesternCommas = (numStr: string): string => {
    const parts = numStr.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
};

/**
 * Formats a decimal number string with Indian commas (1,23,456.7)
 */
const formatIndianDecimal = (numStr: string): string => {
    const parts = numStr.split('.');
    const intPart = parts[0];
    
    if (intPart.length <= 3) {
        return parts.join('.');
    }
    
    const lastThree = intPart.substring(intPart.length - 3);
    const remaining = intPart.substring(0, intPart.length - 3);
    const formattedRemaining = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    parts[0] = `${formattedRemaining},${lastThree}`;
    return parts.join('.');
};

/**
 * Formats a number in Indian numbering system (lakhs, crores)
 * e.g., 1,00,00,000 for 1 crore
 */
export const formatIndianNumber = (num: number): string => {
    const numStr = Math.round(num).toString();
    
    // For numbers less than 1000, use standard formatting
    if (numStr.length <= 3) {
        return numStr;
    }
    
    // Split into last 3 digits and rest
    const lastThree = numStr.substring(numStr.length - 3);
    const remaining = numStr.substring(0, numStr.length - 3);
    
    // Add commas every 2 digits for the remaining part (Indian system)
    const formattedRemaining = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    
    return `${formattedRemaining},${lastThree}`;
};

/**
 * Extracts the LATEST numeric value from a string like "FY21: 10M, FY22: 15M" or "INR 12.5M".
 * Returns 0 if no numeric value is found.
 */
export const extractLatestNumeric = (text: string | null | undefined, defaultValue: number = 0): number => {
    if (!text) return defaultValue;

    // Find all numbers (allowing commas) followed by optional K/M/B
    // CRITICAL: \d+ greedily matches all digits first, handles plain numbers like 45692
    const matches = text.match(/(\d+(?:,\d{3})*)(\.\d+)?\s*([kKmMbB])?/g);
    if (!matches || matches.length === 0) return defaultValue;

    // Take the last one
    const lastMatch = matches[matches.length - 1];
    const numPart = lastMatch.replace(/,/g, '').match(/(\d+\.?\d*)/)?.[1];
    const suffixPart = lastMatch.match(/([kKmMbB])/)?.[1];

    if (!numPart) return defaultValue;
    let val = parseFloat(numPart);
    const suffix = suffixPart?.toLowerCase();
    if (suffix === 'k') val /= 1000;
    if (suffix === 'b') val *= 1000;
    return val;
};

/**
 * Extracts a specific numeric value from a text string.
 * Handles formats like: "$97.5k", "97.5K ARR", "2.5M", "1.2B", "$292,500", etc.
 * @param text - The text to parse
 * @param defaultValue - Default value if parsing fails
 * @param targetUnit - Target unit to normalize to ('absolute', 'k', 'M', 'B'). Defaults to 'M' for backward compatibility.
 * @returns Value in the target unit
 */
export const extractNumericValue = (
    text: string | null | undefined, 
    defaultValue: number = 0,
    targetUnit: ReportingUnit = 'M'
): number => {
    if (!text) return defaultValue;
    
    // Match number with optional decimal, optional commas, and optional suffix (k/M/B)
    // Groups: 1=number part, 2=decimal part, 3=suffix
    // CRITICAL: \d+ greedily matches all digits first, handles plain numbers like 45692
    const match = text.match(/(\d+(?:,\d{3})*)(\.\d+)?\s*([kKmMbB])?/);
    if (!match) return defaultValue;
    
    // Extract the numeric part (remove commas)
    const numberPart = match[1].replace(/,/g, '');
    const decimalPart = match[2] || '';
    const fullNumber = numberPart + decimalPart;
    let val = parseFloat(fullNumber);
    
    if (isNaN(val)) return defaultValue;
    
    // Detect source unit from suffix
    const suffix = match[3]?.toLowerCase();
    let sourceUnit: ReportingUnit = 'absolute'; // Default to absolute if no suffix
    if (suffix === 'k') sourceUnit = 'k';
    else if (suffix === 'm') sourceUnit = 'M';
    else if (suffix === 'b') sourceUnit = 'B';
    
    // Convert to target unit
    return convertUnit(val, sourceUnit, targetUnit);
};

/**
 * Formats a numeric value with currency and suffix (M/B/K).
 */
export const formatFinancialValue = (value: number, currency: string = "USD", decimals: number = 1): string => {
    const symbolMap: { [key: string]: string } = {
        'USD': '$',
        'INR': '₹',
        'EUR': '€',
        'GBP': '£'
    };

    const symbol = symbolMap[currency] || currency + " ";
    return `${symbol}${value.toFixed(decimals)}M`;
};

/**
 * Parses a string containing multiple years of data (e.g., "FY21: 10, FY22: 15")
 * Returns a map of Year -> Value (e.g., { "FY21": 10, "FY22": 15 })
 */
export const parseFinancialTimeSeries = (text: string | null | undefined): Record<string, number> => {
    if (!text) return {};
    const result: Record<string, number> = {};

    // Regex to match "FY21: 10m" or "2021: $10.5M" or "FY25: 45692M" patterns
    // Groups: 1=(FYxx|20xx), 2=Value, 3=Suffix
    // CRITICAL: \d+ greedily matches all leading digits (handles plain numbers like 45692)
    // then (?:,\d{3})* matches optional comma groups (handles 45,692 format)
    const regex = /(FY\d{2}|20\d{2})[:\s-]*[^\d]*(\d+(?:,\d{3})*(?:\.\d+)?)\s*([kKmMbB])?/gi;

    let match;
    while ((match = regex.exec(text)) !== null) {
        const year = match[1].toUpperCase();
        const rawValue = match[2].replace(/,/g, '');
        let value = parseFloat(rawValue);
        const suffix = match[3]?.toLowerCase();

        if (suffix === 'k') value /= 1000;
        if (suffix === 'b') value *= 1000; // Normalizing to M

        if (!isNaN(value)) {
            result[year] = value;
        }
    }

    // If no classic format found, but just a single number, assign to "LATEST"
    if (Object.keys(result).length === 0) {
        const val = extractNumericValue(text);
        if (val > 0) result["LATEST"] = val;
    }

    return result;
};

/**
 * Validates logical consistency: Revenue >= EBITDA >= Net Profit.
 */
export const isFinanciallyConsistent = (revenue: number, ebitda: number, netProfit: number): boolean => {
    if (ebitda > revenue) return false;
    if (netProfit > ebitda) return false;
    return true;
};

/**
 * Calculates Compound Annual Growth Rate (CAGR) from historical financial data.
 * @param timeSeries - Record of year -> value (e.g., { "FY21": 10, "FY22": 12, "FY23": 15 })
 * @returns CAGR as a decimal (e.g., 0.15 for 15%), or null if insufficient data
 */
export const calculateCAGR = (timeSeries: Record<string, number>): number | null => {
    if (!timeSeries || Object.keys(timeSeries).length < 2) {
        return null;
    }

    // CRITICAL: Get current fiscal year to filter out future projections
    const currentCalendarYear = new Date().getFullYear(); // e.g., 2026
    const currentFY = currentCalendarYear - 2000; // e.g., 26

    // Sort years to get chronological order, EXCLUDING future projections
    const sortedEntries = Object.entries(timeSeries)
        .map(([year, value]) => {
            // Extract year number from "FY21" or "2021"
            const yearNum = parseInt(year.replace(/FY|20/g, '')) || parseInt(year);
            return { year: yearNum, value, originalKey: year };
        })
        .filter(entry => {
            // Filter out invalid entries
            if (isNaN(entry.year) || entry.value <= 0) return false;
            // CRITICAL: Filter out future projections - only use actual historical data
            if (entry.year > currentFY) {
                console.log(`📊 [calculateCAGR] Excluding future projection ${entry.originalKey}: ${entry.value}`);
                return false;
            }
            return true;
        })
        .sort((a, b) => a.year - b.year);

    if (sortedEntries.length < 2) {
        console.log(`📊 [calculateCAGR] Not enough historical data points after filtering (${sortedEntries.length})`);
        return null;
    }

    const firstEntry = sortedEntries[0];
    const lastEntry = sortedEntries[sortedEntries.length - 1];
    const firstValue = firstEntry.value;
    const lastValue = lastEntry.value;
    const years = lastEntry.year - firstEntry.year;

    if (years <= 0 || firstValue <= 0) {
        return null;
    }

    // CAGR formula: (Ending Value / Beginning Value)^(1/Years) - 1
    const cagr = Math.pow(lastValue / firstValue, 1 / years) - 1;
    
    // SANITY CHECK: CAGR should be reasonable
    // - Most companies grow 5-25% per year
    // - High-growth startups might hit 50%
    // - Anything above 100% per year is almost certainly an error
    const MAX_REASONABLE_CAGR = 1.0; // 100% max (doubling every year)
    const MIN_REASONABLE_CAGR = -0.5; // -50% min
    
    if (cagr < MIN_REASONABLE_CAGR || cagr > MAX_REASONABLE_CAGR) {
        console.warn(`⚠️ [calculateCAGR] Unreasonable CAGR ${(cagr * 100).toFixed(1)}% detected, returning null`);
        return null;
    }

    console.log(`📊 [calculateCAGR] Calculated ${(cagr * 100).toFixed(1)}% from FY${firstEntry.year} to FY${lastEntry.year}`);
    return cagr;
};

/**
 * Calculates projected organic growth from current revenue to target year.
 * Uses CAGR if available, otherwise uses default annual growth rate.
 * @param currentRevenue - Current revenue in millions
 * @param currentYear - Current fiscal year (e.g., "FY25" or 25)
 * @param targetYear - Target fiscal year (e.g., "FY30" or 30)
 * @param cagr - CAGR as decimal (e.g., 0.15 for 15%), or null to use default
 * @param defaultAnnualGrowth - Default annual growth rate if CAGR not available (default: 0.05 for 5%)
 * @returns Projected organic growth in millions
 */
export const calculateProjectedOrganicGrowth = (
    currentRevenue: number,
    currentYear: string | number,
    targetYear: string | number,
    cagr: number | null = null,
    defaultAnnualGrowth: number = 0.05
): number => {
    if (currentRevenue <= 0) return 0;

    // Extract year numbers
    const currentYearNum = typeof currentYear === 'string' 
        ? parseInt(currentYear.replace(/FY|20/g, '')) || 0
        : currentYear;
    const targetYearNum = typeof targetYear === 'string'
        ? parseInt(targetYear.replace(/FY|20/g, '')) || 0
        : targetYear;

    const years = targetYearNum - currentYearNum;
    if (years <= 0) return 0;

    // SANITY CHECK: Cap CAGR at realistic maximum (50% per year is extreme)
    // Anything higher is likely a calculation error from bad data
    const MAX_REASONABLE_CAGR = 0.50; // 50% annual growth max
    const MIN_REASONABLE_CAGR = -0.30; // -30% decline max
    
    let effectiveGrowthRate = defaultAnnualGrowth;
    
    if (cagr !== null && !isNaN(cagr)) {
        if (cagr > MAX_REASONABLE_CAGR) {
            console.warn(`⚠️ [calculateProjectedOrganicGrowth] CAGR ${(cagr * 100).toFixed(1)}% exceeds max ${MAX_REASONABLE_CAGR * 100}%, capping to reasonable rate`);
            effectiveGrowthRate = MAX_REASONABLE_CAGR;
        } else if (cagr < MIN_REASONABLE_CAGR) {
            console.warn(`⚠️ [calculateProjectedOrganicGrowth] CAGR ${(cagr * 100).toFixed(1)}% below min ${MIN_REASONABLE_CAGR * 100}%, capping to reasonable rate`);
            effectiveGrowthRate = MIN_REASONABLE_CAGR;
        } else if (cagr > 0) {
            effectiveGrowthRate = cagr;
        }
    }

    // Future Value = Present Value * (1 + growth)^years
    const projectedRevenue = currentRevenue * Math.pow(1 + effectiveGrowthRate, years);

    // Return the growth amount (not the total)
    const growth = projectedRevenue - currentRevenue;
    
    // ADDITIONAL SANITY CHECK: Growth shouldn't exceed 10x current revenue over any period
    const maxGrowth = currentRevenue * 10;
    if (growth > maxGrowth) {
        console.warn(`⚠️ [calculateProjectedOrganicGrowth] Growth ${growth.toFixed(1)}M exceeds 10x revenue, capping`);
        return maxGrowth;
    }
    
    return growth;
};
